const PENDING_SKILL_LAUNCH_KEY = 'landing_pending_skill_launch';
const SKILL_UI_PREFILL_KEY = 'skill_ui_prompt_prefill';
const PENDING_SKILL_TTL_MS = 10 * 60 * 1000;

export const MAX_SKILL_PROMPT_LENGTH = 4000;

interface PendingSkillLaunch {
  skillId: string;
  prompt: string;
  createdAt: number;
}

const isPendingSkillLaunch = (value: unknown): value is PendingSkillLaunch => {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Partial<PendingSkillLaunch>;

  return typeof candidate.skillId === 'string'
    && candidate.skillId.length > 0
    && typeof candidate.prompt === 'string'
    && typeof candidate.createdAt === 'number';
};

const parsePendingSkillLaunch = (raw: string): PendingSkillLaunch | null => {
  try {
    const pending: unknown = JSON.parse(raw);

    if (!isPendingSkillLaunch(pending)) return null;
    if (Date.now() - pending.createdAt > PENDING_SKILL_TTL_MS) return null;

    return pending;
  } catch {
    return null;
  }
};

const takePendingSkillLaunch = (key: string): PendingSkillLaunch | null => {
  const raw = sessionStorage.getItem(key);

  if (!raw) return null;
  sessionStorage.removeItem(key);

  return parsePendingSkillLaunch(raw);
};

export const createSkillUiPath = (skillId: string): string => `/skill-store/${encodeURIComponent(skillId)}`;

export const reusedSkillAction = (
  skill: { id: string; interaction_type: 'ui' | 'api' },
): { label: string; path: string } => ({
  label: skill.interaction_type === 'api' ? 'View skill API' : 'Open skill',
  path: createSkillUiPath(skill.id),
});

export const getSkillPromptError = (prompt: string): string | null => (
  prompt.trim().length > MAX_SKILL_PROMPT_LENGTH
    ? `Skill prompts can be at most ${MAX_SKILL_PROMPT_LENGTH.toLocaleString()} characters.`
    : null
);

export const savePendingSkillLaunch = (skillId: string, prompt: string): void => {
  const pending: PendingSkillLaunch = { skillId, prompt, createdAt: Date.now() };

  sessionStorage.setItem(PENDING_SKILL_LAUNCH_KEY, JSON.stringify(pending));
};

export const consumePendingSkillLaunchPath = (): string | null => {
  const pending = takePendingSkillLaunch(PENDING_SKILL_LAUNCH_KEY);

  if (!pending) return null;
  sessionStorage.setItem(SKILL_UI_PREFILL_KEY, JSON.stringify(pending));

  return createSkillUiPath(pending.skillId);
};

export const consumeSkillUiPrompt = (skillId: string): string => {
  const pending = takePendingSkillLaunch(SKILL_UI_PREFILL_KEY);

  return pending?.skillId === skillId ? pending.prompt : '';
};

const PENDING_CHAT_PROMPT_KEY = 'landing_pending_chat_prompt';

/** Remember what an anonymous visitor typed in Chat mode until they have signed in. */
export const savePendingChatPrompt = (prompt: string): void => {
  sessionStorage.setItem(PENDING_CHAT_PROMPT_KEY, JSON.stringify({ prompt, createdAt: Date.now() }));
};

const parsePendingChatPrompt = (raw: string): string | null => {
  try {
    const candidate = JSON.parse(raw) as { prompt?: unknown; createdAt?: unknown };

    if (typeof candidate.prompt !== 'string' || typeof candidate.createdAt !== 'number') return null;

    return Date.now() - candidate.createdAt > PENDING_SKILL_TTL_MS ? null : candidate.prompt;
  } catch {
    return null;
  }
};

export const consumePendingChatPrompt = (): string | null => {
  const raw = sessionStorage.getItem(PENDING_CHAT_PROMPT_KEY);

  if (!raw) return null;
  sessionStorage.removeItem(PENDING_CHAT_PROMPT_KEY);

  return parsePendingChatPrompt(raw);
};


const PENDING_LOOP_PROMPT_KEY = 'landing_pending_loop_prompt';

export interface LoopLaunchOptions {
  browserProfileId?: string; recordVideo?: boolean; modelId?: string;
  watermarkEnabled?: boolean; attachmentIds?: string[];
}
export interface PendingLoopLaunch extends LoopLaunchOptions { initialPrompt: string; }

const loopAttachmentIds = (value: unknown): string[] => Array.isArray(value) ? Array.from(new Set(value.filter(
  (id): id is string => typeof id === 'string' && /^[a-zA-Z0-9_-]{1,100}$/.test(id),
))).slice(0, 10) : [];

export const savePendingLoopPrompt = (prompt: string, options: LoopLaunchOptions = {}): void => {
  const { browserProfileId, recordVideo, modelId, watermarkEnabled, attachmentIds } = options;

  sessionStorage.setItem(PENDING_LOOP_PROMPT_KEY, JSON.stringify({ prompt, browserProfileId, recordVideo, modelId,
    watermarkEnabled, attachmentIds: loopAttachmentIds(attachmentIds), createdAt: Date.now() }));
};

export const consumePendingLoopLaunch = (): PendingLoopLaunch | null => {
  const raw = sessionStorage.getItem(PENDING_LOOP_PROMPT_KEY);

  if (!raw) return null;
  sessionStorage.removeItem(PENDING_LOOP_PROMPT_KEY);
  const initialPrompt = parsePendingChatPrompt(raw);

  if (initialPrompt === null) return null;
  const stored = JSON.parse(raw) as {
    browserProfileId?: unknown; recordVideo?: unknown; modelId?: unknown;
    watermarkEnabled?: unknown; attachmentIds?: unknown;
  };

  return {
    initialPrompt,
    ...(typeof stored.browserProfileId === 'string' && stored.browserProfileId
      ? { browserProfileId: stored.browserProfileId } : {}),
    recordVideo: typeof stored.recordVideo === 'boolean' ? stored.recordVideo : true,
    watermarkEnabled: typeof stored.watermarkEnabled === 'boolean' ? stored.watermarkEnabled : true,
    attachmentIds: loopAttachmentIds(stored.attachmentIds),
    ...(typeof stored.modelId === 'string' && stored.modelId.trim() && stored.modelId.length <= 100
      ? { modelId: stored.modelId.trim() } : {}),
  };
};

export const consumePendingLoopPrompt = (): string | null => consumePendingLoopLaunch()?.initialPrompt ?? null;
